import { useEffect, useRef } from 'react';
import { parseDuration } from '../../pages/Quiz/utils/utils';

/**
 * useQuizTimer — manages the countdown timer for a quiz session.
 *
 * @param {object} options
 * @param {string|null} options.expectedDuration - The quiz's expected duration string (e.g., "01:30:00")
 * @param {string|null} options.startedAt - ISO timestamp when the quiz was started
 * @param {function} options.onTimeUp - Callback invoked when the timer reaches zero
 * @param {string} options.quizType - The quiz type ('assessment', 'exam', 'learning', etc.)
 * @param {function} options.setTimeLeft - State setter to update the displayed time left
 */
function useQuizTimer({ expectedDuration, startedAt, onTimeUp, quizType, setTimeLeft }) {
  const timerRef = useRef();

  useEffect(() => {
    if (!expectedDuration || !startedAt) return;

    const durationSeconds = parseDuration(expectedDuration);
    const startedAtMs = new Date(startedAt).getTime();
    const endAt = startedAtMs + durationSeconds * 1000;

    function updateTimer() {
      const now = Date.now();
      const secondsLeft = Math.max(0, Math.floor((endAt - now) / 1000));
      setTimeLeft(secondsLeft);

      if (secondsLeft <= 0) {
        clearInterval(timerRef.current);
        if (['assessment', 'exam'].includes(quizType)) {
          onTimeUp?.();
        }
      }
    }

    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);

    return () => clearInterval(timerRef.current);
  }, [expectedDuration, startedAt, onTimeUp, quizType, setTimeLeft]);
}

export default useQuizTimer;
